import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import "./index.css";
import "./firebase"; // side-effect init
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Home } from "./routes/Home";
import { LiveApp } from "./routes/live/LiveApp"; // eager — public route, must paint fast

// Catch stray async rejections that don't bubble through React's render path.
// A noisy console is preferable to silent failures — staff can see "what was
// going on" in support tickets.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    // eslint-disable-next-line no-console
    console.error("[unhandledrejection]", e.reason);
  });
  window.addEventListener("error", (e) => {
    // eslint-disable-next-line no-console
    console.error("[window.error]", e.message, e.filename, e.lineno);
  });
}

// Lazy-load admin + operator + display so the public /live bundle stays lean.
// Critically this keeps Konva (admin layout editor only) out of /live entirely.
const AdminApp = lazy(() => import("./routes/admin/AdminApp").then((m) => ({ default: m.AdminApp })));
const OperatorApp = lazy(() => import("./routes/operator/OperatorApp").then((m) => ({ default: m.OperatorApp })));
const DisplayApp = lazy(() => import("./routes/display/DisplayApp").then((m) => ({ default: m.DisplayApp })));

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root");

function Loading(): JSX.Element {
  return <div className="min-h-full grid place-items-center text-fg-muted">Loading…</div>;
}

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="/play/:eventId" element={<OperatorApp />} />
            <Route path="/display/:eventId" element={<DisplayApp />} />
            <Route path="/live/:eventId" element={<LiveApp />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);
